import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { AlertTriangle } from 'lucide-react'
import { GET_AUTOSCHEDULER_REPLY_PROFILE } from '@/lib/graphql/autoscheduler'
import { useAuth } from '@/components/providers/auth-provider'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { writingStatus } from '@/lib/writing-status'
import { cn } from '@/lib/utils'

/**
 * Slim bar over every app surface: is the agent writing as this person, or
 * as a generic bot? Drafts only sound like the user when the profile has
 * texture, so the state stays visible everywhere and the fix (another
 * interview) is one key away. The Angle page hides it: the full card is there.
 *
 * It reads as the app's top bar, not a badge: plain text, the counts as
 * numbers, and the one action as a raised key. Only the generic-bot state
 * is marked, because it is the one worth fixing.
 */
export function AgentIdentityStrip() {
  const location = useLocation()
  const { user } = useAuth()
  const onAngle = location.pathname.startsWith('/angle')
  // The chat is one screen that does not scroll, so the strip is a plain row
  // there; sticky with its phone-header offset it slid over the chat header.
  const barPosition = location.pathname.startsWith('/chat') ? 'relative' : 'sticky top-[73px] md:top-0'
  const isGuest = Boolean(user?.isGuest)

  const { data } = useQuery(GET_AUTOSCHEDULER_REPLY_PROFILE, {
    fetchPolicy: 'cache-first',
    skip: onAngle || isGuest,
  })

  if (onAngle) return null

  const status = writingStatus(data?.getAutoschedulerReplyProfile, isGuest)
  const warn = status.level === 'none'
  const initial = (isGuest ? 'A' : user?.xDisplayName || user?.xUsername || 'Y').slice(0, 1).toUpperCase()

  const keyClass = cn(
    'key ml-auto inline-flex h-7 shrink-0 items-center rounded-md px-2.5 text-xs font-medium',
    warn ? 'key-ink' : 'key-lite text-foreground',
  )

  return (
    <div className={`${barPosition} z-30 border-b border-border/60 bg-background/95 backdrop-blur`}>
      <div className="flex h-9 items-center gap-3 px-4 text-[13px] sm:h-10 sm:px-6">
        {warn ? (
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden="true" />
        ) : (
          <Avatar className="h-5 w-5 shrink-0">
            {!isGuest && user?.xProfileImage && <AvatarImage src={user.xProfileImage} alt="" />}
            <AvatarFallback className="bg-muted text-[10px] font-semibold text-foreground">{initial}</AvatarFallback>
          </Avatar>
        )}

        <p className="min-w-0 truncate">
          <span className="text-muted-foreground">Writing as </span>
          <span className={cn('font-medium', warn ? 'text-amber-800 dark:text-amber-300' : 'text-foreground')}>{status.name}</span>
          {status.note && <span className="text-muted-foreground"> ({status.note})</span>}
        </p>

        {status.level === 'guest' && (
          <span className="hidden truncate text-xs text-muted-foreground lg:inline">
            a made-up founder, so the demo has a voice to write in
          </span>
        )}

        {status.hasAngle && (
          <span className="hidden items-center gap-3 text-xs text-muted-foreground md:flex" aria-label="On file for your voice">
            <span><span className="font-semibold tabular-nums text-foreground">{status.stories}</span> {status.stories === 1 ? 'story' : 'stories'}</span>
            <span><span className="font-semibold tabular-nums text-foreground">{status.facts}</span> {status.facts === 1 ? 'fact' : 'facts'}</span>
            <span><span className="font-semibold tabular-nums text-foreground">{status.rules}</span> voice {status.rules === 1 ? 'rule' : 'rules'}</span>
          </span>
        )}

        {/* Guests already have the demo bar's Sign in key above; one sign-in action is enough. */}
        {!isGuest && (
          <Link to="/angle" className={keyClass}>
            {status.cta}
          </Link>
        )}
      </div>
    </div>
  )
}
